import React from 'react';

interface MetricsGridProps {
  workout: {
    duration?: number;
    duration_minutes?: number;
    distance_km?: number;
    estimated_distance?: number;
    activity_type?: string;
    activityType?: string;
    type?: string;
    workout_type?: string;
    exercises?: unknown[] | { length?: number }[];
    phases?: unknown[] | { exercises?: unknown[]; exercises_json?: unknown[] }[];
    workout_data?: {
      estimated_calories?: number;
      elevation_gain_m?: number;
      elevation_loss_m?: number;
      total_volume_kg?: number;
      rounds_target?: string;
      phases?: unknown[] | { hr_zone?: number }[];
      exercises?: unknown[];
      pace_target?: string;
      target_pace?: string;
      hr_zone?: number;
      hiit_structure?: {
        rounds_target?: string;
      };
    };
  };
  userHrZones?: {
    zone1Min: number;
    zone1Max: number;
    zone2Min: number;
    zone2Max: number;
    zone3Min: number;
    zone3Max: number;
    zone4Min: number;
    zone4Max: number;
    zone5Min: number;
    zone5Max: number;
  } | null;
}

export const PrimaryMetricsGrid: React.FC<MetricsGridProps> = ({ workout, userHrZones }) => {
  // 🐛 DIAGNOSTIC (Jan 21, 2026): Debug workout object received
  console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  console.log('🔍 PrimaryMetricsGrid RECEIVED WORKOUT');
  console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  console.log('workout object:', workout);
  console.log('');
  console.log('🔎 WORKOUT PROPERTY AUDIT:');
  Object.keys(workout).forEach(key => {
    const value = workout[key as keyof typeof workout];
    const type = typeof value;
    const isArray = Array.isArray(value);
    const isObject = type === 'object' && !isArray && value !== null;
    const isEmpty = isObject && value != null && typeof value === 'object' && Object.keys(value).length === 0;
    
    console.log(`  workout.${key}:`, {
      type: isArray ? 'array' : type,
      value: isEmpty ? '⚠️ EMPTY OBJECT {}' : (isArray ? `[${value.length} items]` : value),
      isEmpty: isEmpty ? '⚠️ YES - WILL CAUSE REACT ERROR #31' : 'no'
    });
  });
  
  if (workout.workout_data) {
    console.log('');
    console.log('🔎 WORKOUT_DATA NESTED AUDIT:');
    Object.keys(workout.workout_data).forEach(key => {
      const value = workout.workout_data![key as keyof typeof workout.workout_data];
      const type = typeof value;
      const isArray = Array.isArray(value);
      const isObject = type === 'object' && !isArray && value !== null;
      const isEmpty = isObject && value != null && typeof value === 'object' && Object.keys(value).length === 0;
      
      console.log(`  workout_data.${key}:`, {
        type: isArray ? 'array' : type,
        value: isEmpty ? '⚠️ EMPTY OBJECT {}' : (isArray ? `[${value.length} items]` : value),
        isEmpty: isEmpty ? '⚠️ YES - WILL CAUSE REACT ERROR #31' : 'no'
      });
    });
  }
  console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  
  // 🔧 JAN 21, 2026: Safeguard against empty objects {} in workout properties
  const activityType = typeof workout.activityType === 'string' ? workout.activityType :
                       typeof workout.activity_type === 'string' ? workout.activity_type : 'cardio';
  const workoutType = typeof workout.type === 'string' ? workout.type :
                      typeof workout.workout_type === 'string' ? workout.workout_type : 'cardio';
  const duration = typeof workout.duration_minutes === 'number' ? workout.duration_minutes :
                   typeof workout.duration === 'number' ? workout.duration : 0;
  const phases = Array.isArray(workout.workout_data?.phases) ? workout.workout_data.phases :
                 Array.isArray(workout.phases) ? workout.phases : [];
  
  // Determine which metrics to show based on workout type
  const isCardio = activityType === 'cardio' || activityType === 'interval';
  const isStrength = activityType === 'strength' || workoutType?.includes('strength');
  const isHIIT = activityType === 'hiit' || workoutType?.includes('hiit');
  const isMobility = activityType === 'mobility' || workoutType?.includes('mobility') || workoutType?.includes('yoga');

  const metrics: Array<{
    label: string;
    value: string | number;
    icon: string;
    show: boolean;
  }> = [];

  // 🚨 JAN 22, 2026: Do NOT show duration (redundant with header)
  // Duration is already shown in header below title
  // If no other metrics exist, component returns NULL (graceful)

  // Cardio/Interval specific metrics
  if (isCardio) {
    // 📏 SPRINT 3 (JAN 2026): Use estimated_distance from calculator
    const distance = workout.estimated_distance || workout.distance_km;
    if (distance && distance > 0) {
      metrics.push({
        label: 'Distance',
        value: `${distance.toFixed(1)} km`,  // 🔧 Remove tilde (~) for cleaner display
        icon: '📏',
        show: true
      });
    }
    
    // 🚨 JAN 29, 2026: REMOVED Target Pace from data grid
    // User feedback: "Pace makes no sense in the grid"
    // Pace details are shown in Run Plan section with phase-by-phase breakdowns
    // Data grid should show high-level workout metrics only (distance, calories, HR zone)
    
    // 🆕 AUDIT FIX JAN 19: Add HR zone from phases or workout_data
    const hrZone = workout.workout_data?.hr_zone || 
                  (() => {
                    // Get most common zone from phases
                    if (phases.length > 0) {
                      const zones = phases
                        .map((p: any) => p.hr_zone)
                        .filter((z: number) => z && z > 0);
                      if (zones.length > 0) {
                        // Return most common zone (for easy runs, should be consistent)
                        return zones[0];
                      }
                    }
                    return null;
                  })();
    
    if (hrZone && userHrZones) {
      // Get zone BPM range
      const getZoneBPM = (zone: number): { min: number; max: number; name: string } => {
        switch (zone) {
          case 1: return { min: userHrZones.zone1Min, max: userHrZones.zone1Max, name: 'Recovery' };
          case 2: return { min: userHrZones.zone2Min, max: userHrZones.zone2Max, name: 'Easy' };
          case 3: return { min: userHrZones.zone3Min, max: userHrZones.zone3Max, name: 'Tempo' };
          case 4: return { min: userHrZones.zone4Min, max: userHrZones.zone4Max, name: 'Threshold' };
          case 5: return { min: userHrZones.zone5Min, max: userHrZones.zone5Max, name: 'Max' };
          default: return { min: 0, max: 0, name: 'Unknown' };
        }
      };
      
      const zoneBPM = getZoneBPM(hrZone);
      metrics.push({
        label: 'Avg HR Zone',
        value: `Zone ${hrZone}: ${zoneBPM.name} (${zoneBPM.min}-${zoneBPM.max})`,
        icon: '❤️',
        show: true
      });
    }
    
    if (workout.workout_data?.estimated_calories) {
      metrics.push({
        label: 'Est. Calories',
        value: `${workout.workout_data.estimated_calories} kcal`,
        icon: '🔥',
        show: true
      });
    }
    
    if (workout.workout_data?.elevation_gain_m) {
      metrics.push({
        label: 'Elevation Gain',
        value: `${workout.workout_data.elevation_gain_m}m`,
        icon: '⛰️',
        show: true
      });
    }
  }

  // Strength specific metrics
  if (isStrength) {
    // 🆕 AUDIT FIX JAN 19: Count exercises WITHIN phases, not top-level array
    // 🔧 JAN 22, 2026: Check BOTH exercises and exercises_json in phases
    // 🐛 JAN 29, 2026: CRITICAL FIX - Don't early return, check fallbacks if phase count is 0
    const totalExercises = (() => {
      let count = 0;
      
      // TRY phase-nested exercises first (ideal structure)
      if (workout.workout_data?.phases && Array.isArray(workout.workout_data.phases)) {
        count = workout.workout_data.phases.reduce((sum: number, phase: any) => 
          sum + (phase.exercises?.length || phase.exercises_json?.length || 0), 0
        );
      }
      
      // If phases had exercises, use that count
      if (count > 0) return count;
      
      // 🔧 FALLBACK: Check workout.phases (alternative location)
      if (workout.phases && Array.isArray(workout.phases)) {
        count = workout.phases.reduce((sum: number, phase: any) => 
          sum + (phase.exercises?.length || phase.exercises_json?.length || 0), 0
        );
        if (count > 0) return count;
      }
      
      // 🔧 FALLBACK: Check root-level exercise arrays (flat structure)
      if (workout.workout_data?.exercises?.length) {
        return workout.workout_data.exercises.length;
      }
      if (workout.exercises?.length) {
        return workout.exercises.length;
      }
      
      return 0;
    })();
    
    if (totalExercises > 0) {
      metrics.push({
        label: 'Exercises',
        value: totalExercises,
        icon: '💪',
        show: true
      });
    }
    
    // 🔧 FEB 1, 2026: REMOVED Est. Volume
    // Framework analysis shows volume is NOT a tracked KPI for supplementary strength work
    // For running frameworks (half marathon rebuild), strength is support work (injury prevention)
    // No volume progression prescribed in framework phases or milestones
    // User: 930c9550-58fb-4ab6-9059-3af3d6d09af8, Framework: "2:20 Half Marathon (Post‑Chemo Rebuild)"
    // if (workout.workout_data?.total_volume_kg) { ... }
    
    if (workout.workout_data?.estimated_calories) {
      metrics.push({
        label: 'Est. Calories',
        value: `${workout.workout_data.estimated_calories} kcal`,
        icon: '🔥',
        show: true
      });
    }
  }

  // HIIT specific metrics
  if (isHIIT) {
    const roundsTarget = workout.workout_data?.hiit_structure?.rounds_target || workout.workout_data?.rounds_target;
    // 🔧 JAN 21, 2026: CRITICAL - Ensure roundsTarget is string/number before rendering
    if (roundsTarget && (typeof roundsTarget === 'string' || typeof roundsTarget === 'number')) {
      metrics.push({
        label: 'Target Rounds',
        value: String(roundsTarget),
        icon: '🔄',
        show: true
      });
    }
    
    if (workout.workout_data?.estimated_calories) {
      metrics.push({
        label: 'Est. Calories',
        value: `${workout.workout_data.estimated_calories} kcal`,
        icon: '🔥',
        show: true
      });
    }
  }

  // Mobility specific metrics
  if (isMobility) {
    const totalMovements = workout.exercises?.length || workout.workout_data?.phases?.length || 0;
    if (totalMovements > 0) {
      metrics.push({
        label: 'Movements',
        value: totalMovements,
        icon: '🤸',
        show: true
      });
    }
  }

  // Filter to only shown metrics
  const visibleMetrics = metrics.filter(m => m.show);

  // 🐛 DEBUG (Jan 22, 2026): Log metrics calculation
  console.log('');
  console.log('🔎 METRICS CALCULATION:');
  console.log('  Total metrics built:', metrics.length);
  console.log('  Visible metrics:', visibleMetrics.length);
  console.log('  Metrics array:', visibleMetrics);
  console.log('  isCardio:', isCardio);
  console.log('  isStrength:', isStrength);
  console.log('  isHIIT:', isHIIT);
  console.log('  isMobility:', isMobility);
  console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

  // 🔧 FIX JAN 23, 2026: Return NULL gracefully if no metrics
  // Duration already shown in header, so metrics grid not needed
  if (visibleMetrics.length === 0) {
    console.log('✅ PrimaryMetricsGrid: No metrics to show (duration in header)');
    return null;
  }

  // 🔧 FEB 1, 2026: Render as compact horizontal pills (not large boxes)
  // Space reduction: 100px → 40px (60% savings)
  // Applies to: Strength (Exercises, Calories), HIIT (Rounds, Calories), Mobility (Movements)
  return (
    <div className="mx-4 mb-4 flex items-center gap-2 flex-wrap">
      {visibleMetrics.map((metric, index) => {
        // Color mapping by metric type
        const colorClass = 
          metric.label === 'Exercises' ? 'bg-purple-50 text-purple-700 border-purple-200' :
          metric.label === 'Est. Calories' ? 'bg-orange-50 text-orange-700 border-orange-200' :
          metric.label === 'Movements' ? 'bg-teal-50 text-teal-700 border-teal-200' :
          metric.label === 'Target Rounds' ? 'bg-blue-50 text-blue-700 border-blue-200' :
          'bg-gray-50 text-gray-700 border-gray-200';
        
        return (
          <span 
            key={index}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border ${colorClass}`}
          >
            <span>{metric.icon}</span>
            <span className="font-semibold">{metric.value}</span>
            <span className="text-xs opacity-70">{metric.label}</span>
          </span>
        );
      })}
    </div>
  );
};
